import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, ArrowRight, BookOpen } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface Item {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  image_url: string | null;
  published_at: string | null;
}

const formatDate = (iso: string | null) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return null;
  }
};

interface LatestGuidesProps {
  limit?: number;
  title?: string;
  subtitle?: string;
}

const LatestGuides = ({
  limit = 3,
  title = "Latest articles & tips",
  subtitle = "Our latest guides and tips to plan your trip to Morocco.",
}: LatestGuidesProps) => {
  const [items, setItems] = useState<Item[]>([]);

  useEffect(() => {
    (async () => {
      const { data: arts } = await (supabase as any)
        .from("blog_articles")
        .select("id,slug,title,excerpt,published_at")
        .eq("status", "published")
        .order("published_at", { ascending: false })
        .limit(limit);
      const list = arts || [];
      const ids = list.map((a: any) => a.id);
      let imgs: any[] = [];
      if (ids.length) {
        const { data } = await (supabase as any)
          .from("blog_article_images")
          .select("article_id,image_url,slot")
          .in("article_id", ids)
          .order("slot");
        imgs = data || [];
      }
      setItems(
        list.map((a: any) => ({
          ...a,
          image_url: imgs.find((i) => i.article_id === a.id)?.image_url || null,
        }))
      );
    })();
  }, [limit]);

  if (items.length === 0) return null;

  return (
    <section className="py-20 bg-muted/40" aria-labelledby="latest-guides-heading">
      <div className="container mx-auto px-6">
        <div className="text-center mb-12">
          <span className="inline-flex items-center gap-2 rounded-full bg-accent/15 px-4 py-1.5 text-sm font-semibold text-primary mb-4">
            <BookOpen className="h-4 w-4" />
            Travel Guide
          </span>
          <h2
            id="latest-guides-heading"
            className="font-heading text-3xl md:text-4xl font-bold text-primary"
          >
            {title}
          </h2>
          <p className="mt-3 text-muted-foreground max-w-2xl mx-auto">
            {subtitle}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {items.map((it) => {
            const date = formatDate(it.published_at);
            return (
              <article
                key={it.id}
                itemScope
                itemType="https://schema.org/BlogPosting"
                className="group flex flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300"
              >
                <Link
                  to={`/guide/blog/${it.slug}`}
                  aria-label={it.title}
                  className="block relative overflow-hidden aspect-video bg-muted"
                >
                  {it.image_url ? (
                    <img
                      src={it.image_url}
                      alt={it.title}
                      itemProp="image"
                      loading="lazy"
                      decoding="async"
                      width={600}
                      height={338}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                    />
                  ) : (
                    <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-accent/10" />
                  )}
                </Link>
                <div className="flex flex-1 flex-col p-5 space-y-3">
                  <h3
                    itemProp="headline"
                    className="font-heading text-lg font-bold text-foreground leading-snug line-clamp-2"
                  >
                    <Link
                      to={`/guide/blog/${it.slug}`}
                      className="hover:text-primary transition-colors"
                    >
                      {it.title}
                    </Link>
                  </h3>
                  {it.excerpt && (
                    <p
                      itemProp="description"
                      className="text-sm text-muted-foreground leading-relaxed line-clamp-2"
                    >
                      {it.excerpt}
                    </p>
                  )}
                  <div className="mt-auto pt-3 flex items-center justify-between gap-3 text-xs text-muted-foreground border-t border-border/60">
                    {date && (
                      <span className="inline-flex items-center gap-1.5">
                        <Calendar className="h-3.5 w-3.5" />
                        <time
                          itemProp="datePublished"
                          dateTime={it.published_at ?? undefined}
                        >
                          {date}
                        </time>
                      </span>
                    )}
                    <Link
                      to={`/guide/blog/${it.slug}`}
                      className="inline-flex items-center gap-1 font-semibold text-primary hover:gap-2 transition-all"
                    >
                      Lire l'article <ArrowRight className="h-3.5 w-3.5" />
                    </Link>
                  </div>
                </div>
              </article>
            );
          })}
        </div>

        <div className="mt-10 text-center">
          <Link
            to="/guide"
            className="inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3 text-sm font-semibold text-primary-foreground shadow-md hover:bg-primary/90 hover:gap-3 transition-all"
          >
            See all our guides <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default LatestGuides;
